import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Check, Edit3, Save, X, Plus, Trash2, Loader2, Minus, Ban, ChevronUp, ChevronDown, List, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getItemText = (item) => (typeof item === 'string' ? item : item?.text || '');
const getSubItems = (item) => (typeof item === 'object' && item?.subItems ? item.subItems : null);
const hasSubItems = (item) => {
    const subs = getSubItems(item);
    return subs && subs.length > 0;
};
const itemHasInput = (item) => typeof item === 'object' && item?.hasInput;

const countCheckable = (sections) => {
    let total = 0;
    for (const s of sections) {
        for (const item of (s.items || [])) {
            if (hasSubItems(item)) {
                total += item.subItems.length;
            } else {
                total += 1;
            }
        }
    }
    return total;
};

export default function GuideChecklistDepartPage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState({});
    const [notes, setNotes] = useState({});
    const [editing, setEditing] = useState(false);
    const [editSections, setEditSections] = useState([]);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchPage = useCallback(async () => {
        try {
            const res = await authFetch(`${API}/api/guide/pages/checklist-depart`);
            const data = await res.json();
            setPage(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const toggle = (key, value) => {
        setChecked(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
    };

    const sections = page?.sections || [];
    const totalItems = countCheckable(sections);
    const answeredCount = Object.values(checked).filter(v => v != null).length;

    const startEdit = () => {
        setEditSections(JSON.parse(JSON.stringify(sections)));
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authFetch(`${API}/api/guide/pages/checklist-depart`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sections: editSections }),
            });
            const data = await res.json();
            setPage(data);
            setEditing(false);
            setChecked({});
            toast.success('Fiche mise a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    const addSection = () => setEditSections(prev => [...prev, { id: '', title: '', items: [] }]);
    const removeSection = (idx) => setEditSections(prev => prev.filter((_, i) => i !== idx));
    const updateSectionTitle = (idx, value) => {
        setEditSections(prev => { const c = [...prev]; c[idx] = { ...c[idx], title: value }; return c; });
    };
    const addItem = (sIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.push('');
            return c;
        });
    };
    const updateItem = (sIdx, iIdx, value) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const item = c[sIdx].items[iIdx];
            if (typeof item === 'object') {
                c[sIdx].items[iIdx] = { ...item, text: value };
            } else {
                c[sIdx].items[iIdx] = value;
            }
            return c;
        });
    };
    const removeItem = (sIdx, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.splice(iIdx, 1);
            return c;
        });
    };
    const moveSection = (idx, dir) => {
        setEditSections(prev => {
            const c = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= c.length) return c;
            [c[idx], c[target]] = [c[target], c[idx]];
            return c;
        });
    };
    const moveItem = (sIdx, iIdx, dir) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const items = c[sIdx].items;
            const target = iIdx + dir;
            if (target < 0 || target >= items.length) return c;
            [items[iIdx], items[target]] = [items[target], items[iIdx]];
            return c;
        });
    };
    const toggleSubItems = (sIdx, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const item = c[sIdx].items[iIdx];
            if (typeof item === 'object' && item.subItems) {
                c[sIdx].items[iIdx] = item.text || '';
            } else {
                const text = typeof item === 'string' ? item : item?.text || '';
                c[sIdx].items[iIdx] = { text, subItems: [''] };
            }
            return c;
        });
    };
    const addSubItem = (sIdx, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items[iIdx].subItems.push('');
            return c;
        });
    };
    const updateSubItem = (sIdx, iIdx, siIdx, value) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items[iIdx].subItems[siIdx] = value;
            return c;
        });
    };
    const removeSubItem = (sIdx, iIdx, siIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items[iIdx].subItems.splice(siIdx, 1);
            if (c[sIdx].items[iIdx].subItems.length === 0) {
                c[sIdx].items[iIdx] = c[sIdx].items[iIdx].text || '';
            }
            return c;
        });
    };

    const StatusButtons = ({ checkKey }) => {
        const status = checked[checkKey] || null;
        return (
            <div className="flex gap-px flex-shrink-0">
                <button onClick={() => toggle(checkKey, 'fait')}
                    className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${status === 'fait' ? 'bg-emerald-500 text-white' : 'border border-slate-300 text-slate-400 hover:border-emerald-400'}`}
                    title="Fait"><Check className="w-2.5 h-2.5" /></button>
                <button onClick={() => toggle(checkKey, 'pas_necessaire')}
                    className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${status === 'pas_necessaire' ? 'bg-amber-400 text-white' : 'border border-slate-300 text-slate-400 hover:border-amber-400'}`}
                    title="Pas necessaire"><Minus className="w-2.5 h-2.5" /></button>
                <button onClick={() => toggle(checkKey, 'a_ne_pas_faire')}
                    className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${status === 'a_ne_pas_faire' ? 'bg-red-500 text-white' : 'border border-slate-300 text-slate-400 hover:border-red-400'}`}
                    title="A ne pas faire"><Ban className="w-2.5 h-2.5" /></button>
            </div>
        );
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    if (editing) {
        return (
            <div className="px-4 py-5" data-testid="guide-checklist-depart-edit">
                <h2 className="text-base font-bold text-slate-900 mb-3">Modifier la fiche de depart</h2>
                <div className="space-y-3">
                    {editSections.map((section, si) => (
                        <div key={si} className="bg-white rounded-xl border border-slate-200 p-3">
                            <div className="flex items-center gap-1 mb-2">
                                <div className="flex flex-col">
                                    <button onClick={() => moveSection(si, -1)} disabled={si === 0} className={si === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600'}><ChevronUp className="w-4 h-4" /></button>
                                    <button onClick={() => moveSection(si, 1)} disabled={si === editSections.length - 1} className={si === editSections.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600'}><ChevronDown className="w-4 h-4" /></button>
                                </div>
                                <Input value={section.title} onChange={e => updateSectionTitle(si, e.target.value)} placeholder="Categorie..." className="h-8 text-sm font-semibold flex-1" />
                                <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <div className="space-y-1 pl-2">
                                {(section.items || []).map((item, ii) => {
                                    const isObj = typeof item === 'object' && item?.subItems;
                                    const text = getItemText(item);
                                    return (
                                        <div key={ii}>
                                            <div className="flex items-center gap-1">
                                                <div className="flex flex-col">
                                                    <button onClick={() => moveItem(si, ii, -1)} disabled={ii === 0} className={ii === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600'}><ChevronUp className="w-3 h-3" /></button>
                                                    <button onClick={() => moveItem(si, ii, 1)} disabled={ii === (section.items?.length || 0) - 1} className={ii === (section.items?.length || 0) - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600'}><ChevronDown className="w-3 h-3" /></button>
                                                </div>
                                                <Input value={text} onChange={e => updateItem(si, ii, e.target.value)} placeholder="Item..." className="h-7 text-xs flex-1" />
                                                <button onClick={() => toggleSubItems(si, ii)} title={isObj ? 'Supprimer sous-items' : 'Ajouter sous-items'} className={`${isObj ? 'text-indigo-500' : 'text-slate-300 hover:text-indigo-400'}`}><List className="w-3.5 h-3.5" /></button>
                                                <button onClick={() => removeItem(si, ii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                            </div>
                                            {isObj && (
                                                <div className="pl-8 space-y-1 mt-1">
                                                    {item.subItems.map((si2, sii) => (
                                                        <div key={sii} className="flex items-center gap-1">
                                                            <span className="text-slate-300 text-[10px]">-</span>
                                                            <Input value={si2} onChange={e => updateSubItem(si, ii, sii, e.target.value)} placeholder="Sous-item..." className="h-6 text-[11px] flex-1" />
                                                            <button onClick={() => removeSubItem(si, ii, sii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-2.5 h-2.5" /></button>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => addSubItem(si, ii)} className="text-[10px] text-slate-400 hover:text-slate-600 pl-3">+ sous-item</button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full" onClick={() => addItem(si)}>
                                    <Plus className="w-3 h-3 mr-1" />Item
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full" onClick={addSection}>
                        <Plus className="w-4 h-4 mr-1" />Categorie
                    </Button>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                        <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 py-5" data-testid="guide-checklist-depart">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-2">
                <div>
                    <h1 className="text-lg font-bold text-slate-900">{page?.title || 'Fiche de depart'}</h1>
                    <p className="text-xs text-slate-500">{page?.subtitle || 'Check-list de fermeture'}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-bold">
                        {answeredCount}/{totalItems}
                    </div>
                    <Button size="sm" variant="ghost" className="text-slate-400 p-1" onClick={startEdit} data-testid="edit-page-btn">
                        <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </div>

            <div className="w-full bg-slate-200 rounded-full h-1.5 mb-2">
                <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${totalItems > 0 ? (answeredCount / totalItems) * 100 : 0}%` }} />
            </div>

            <div className="flex items-center justify-center gap-3 mb-2 text-[9px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500 inline-block" /> Fait</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400 inline-block" /> Pas necessaire</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500 inline-block" /> A ne pas faire</span>
            </div>

            <div className="space-y-2">
                {sections.map(section => (
                    <div key={section.id} className="bg-white rounded-lg border border-slate-300 shadow-sm overflow-hidden">
                        <div className="bg-indigo-50 px-2.5 py-1 border-b border-indigo-200">
                            <h2 className="font-semibold text-indigo-800 text-[11px]">{section.title}</h2>
                        </div>
                        <div>
                            {(section.items || []).map((item, j) => {
                                if (hasSubItems(item)) {
                                    return (
                                        <div key={j}>
                                            <div className={`pl-8 pr-2.5 py-[5px] ${j > 0 ? 'border-t border-slate-200' : ''}`}>
                                                <span className="text-sm leading-none font-medium text-slate-800">{getItemText(item)}</span>
                                            </div>
                                            {item.subItems.map((sub, k) => {
                                                const checkKey = `${section.id}-${j}-${k}`;
                                                const status = checked[checkKey] || null;
                                                return (
                                                    <div key={k} className="pl-14 pr-2.5 py-[4px] flex items-center gap-1.5 border-t border-slate-100" data-testid={`checklist-item-${section.id}-${j}-${k}`}>
                                                        <span className={`flex-1 text-[13px] leading-none ${status ? 'text-slate-400' : 'text-slate-600'}`}>{sub}</span>
                                                        <StatusButtons checkKey={checkKey} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }
                                const checkKey = `${section.id}-${j}`;
                                const status = checked[checkKey] || null;
                                return (
                                    <div key={j} className={`pl-8 pr-2.5 py-[5px] ${j > 0 ? 'border-t border-slate-100' : ''}`} data-testid={`checklist-item-${section.id}-${j}`}>
                                        <div className="flex items-center gap-1.5">
                                            <span className={`flex-1 text-sm leading-none ${status ? 'text-slate-400' : 'text-slate-700'}`}>{getItemText(item)}</span>
                                            <StatusButtons checkKey={checkKey} />
                                        </div>
                                        {itemHasInput(item) && (
                                            <input
                                                type="text"
                                                value={notes[checkKey] || ''}
                                                onChange={e => setNotes(prev => ({ ...prev, [checkKey]: e.target.value }))}
                                                placeholder="Preciser..."
                                                className="mt-1 w-full border border-slate-200 rounded px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                                                data-testid={`note-input-${section.id}-${j}`}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {answeredCount === totalItems && totalItems > 0 && (
                <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                    <p className="text-emerald-700 font-semibold text-sm">Check-list complete !</p>
                </div>
            )}

            {answeredCount > 0 && (
                <Button
                    className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white"
                    onClick={async () => {
                        setSubmitting(true);
                        try {
                            const user = JSON.parse(localStorage.getItem('guide_user') || '{}');
                            const sectionsSummary = sections.map(s => {
                                const items = (s.items || []).flatMap((item, j) => {
                                    if (hasSubItems(item)) {
                                        return item.subItems.map((sub, k) => ({
                                            label: typeof sub === 'string' ? sub : sub?.text || '',
                                            parent: getItemText(item),
                                            status: checked[`${s.id}-${j}-${k}`] || null,
                                            note: notes[`${s.id}-${j}-${k}`] || null,
                                        }));
                                    }
                                    return [{ label: getItemText(item), parent: null, status: checked[`${s.id}-${j}`] || null, note: notes[`${s.id}-${j}`] || null }];
                                });
                                return { title: s.title, items };
                            });
                            const site = JSON.parse(localStorage.getItem('guide_selected_site') || 'null');
                            await authFetch(`${API}/api/guide/checklist-results`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    slug: 'checklist-depart',
                                    user_name: user.name || user.email || 'Inconnu',
                                    user_email: user.email || '',
                                    site_id: site?.id || null,
                                    site_name: site?.name || null,
                                    date: new Date().toISOString(),
                                    results: checked,
                                    notes: notes,
                                    sections_summary: sectionsSummary,
                                    total_items: totalItems,
                                    answered_count: answeredCount,
                                }),
                            });
                            toast.success('Fiche validée et enregistrée');
                            setChecked({});
                            setNotes({});
                        } catch { toast.error('Erreur lors de la validation'); }
                        finally { setSubmitting(false); }
                    }}
                    disabled={submitting}
                    data-testid="validate-checklist-btn"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Valider la fiche ({answeredCount}/{totalItems})
                </Button>
            )}
        </div>
    );
}
